import { create } from 'zustand';

import { CareServiceOrgansKey, TextToStructuredObservation } from '@/api';
import { FindingsByOrgan, UpdateFindingDetails } from '@/shared/model';

import { DefaultFindingsState } from './use-default-finding.model';

export const useDefaultFindingStore = create<DefaultFindingsState>((set, get) => ({
  addDefaultFinding: (finding: TextToStructuredObservation, shouldHighlight = false) => {
    set((state) => {
      const updatedFindings = { ...state.defaultFindings };
      const organ = finding.conditionTemplate?.organ;

      if (organ) {
        if (!updatedFindings[organ]) {
          updatedFindings[organ] = [];
        }

        updatedFindings[organ] = [...updatedFindings[organ], finding];

        return {
          ...state,
          defaultFindings: updatedFindings,
          highlightedFinding: shouldHighlight ? finding : state.highlightedFinding,
        };
      }

      return state;
    });

    return get().defaultFindings;
  },
  defaultFindings: {} as FindingsByOrgan,
  deleteDefaultFinding: (organ: CareServiceOrgansKey, uuid: string) => {
    set((state) => {
      const organFindings = state.defaultFindings[organ] || [];
      const updatedFindings = organFindings.filter(
        (finding) => finding.renderedSummary.uuid !== uuid,
      );

      return {
        defaultFindings: {
          ...state.defaultFindings,
          [organ]: updatedFindings,
        },
      };
    });

    return get().defaultFindings;
  },
  getDefaultFindingsByOrgan: (organ: CareServiceOrgansKey) => get().defaultFindings[organ] || [],
  highlightedFinding: null,
  setDefaultFindings: (defaultFindings: FindingsByOrgan) => {
    set((state) => ({
      defaultFindings: {
        ...state.defaultFindings,
        ...defaultFindings,
      },
    }));
  },
  setHighlightedFinding: (finding: TextToStructuredObservation) => {
    set({ highlightedFinding: finding });
  },
  updateDefaultFinding: (
    organ: CareServiceOrgansKey,
    updatedFinding: UpdateFindingDetails,
    renderedSummaryId: string,
  ) => {
    set((state) => {
      const organFindings = state.defaultFindings[organ]?.map((finding) => {
        const isSameUuid = finding.renderedSummary.uuid === renderedSummaryId;

        if (isSameUuid) {
          return {
            ...finding,
            ...updatedFinding,
          };
        }

        return finding;
      });

      return {
        ...state,
        defaultFindings: {
          ...state.defaultFindings,
          [organ]: organFindings || [updatedFinding],
        },
      };
    });

    return get().defaultFindings;
  },
}));
